import { ReactNode, useCallback, useState } from "react";
import Skeleton from "react-loading-skeleton";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Card from "ds/components/Card";
import Icon from "ds/components/Icon";
import ProgressBar from "ds/components/ProgressBar";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import TileWrapper from "ds/components/Tile/Wrapper";
import TileContent from "ds/components/Tile/Content";
import EmptyState from "ds/components/EmptyState";
import MissingDataBanner from "components/MissingDataBanner";
import { Clock, ConfettiColored, GraduationHat } from "components/icons/generated";
import useUserGuideProgressMutations from "shared/UserGuides/useUserGuideProgressMutations";
import { USER_GUIDE_START_EVENT_NAME, UserGuideStartLocation } from "shared/UserGuides/analytics";
import useAnalytics from "hooks/useAnalytics";
import useOpenLearnGuide from "components/AssistantPanel/Learn/useOpenLearnGuide";
import { UserGuideStatus } from "types/generated";
import Tooltip from "ds/components/Tooltip";

import useMissionsProgressData from "./useData";

type MissionControlMissionsProgressProps = {
  headerSuffix?: ReactNode;
};

const MissionControlMissionsProgress = ({ headerSuffix }: MissionControlMissionsProgressProps) => {
  const {
    overallTimeToComplete,
    completedCount,
    totalCount,
    nextObjective,
    isAllCompleted,
    loading,
    error,
    refetch,
  } = useMissionsProgressData();

  const openLearnPanel = useOpenLearnGuide();
  const { startUserGuide } = useUserGuideProgressMutations();
  const [isStarting, setIsStarting] = useState(false);

  const trackSegmentEvent = useAnalytics();

  const handleStart = useCallback(async () => {
    if (!nextObjective) return;
    setIsStarting(true);
    trackSegmentEvent(USER_GUIDE_START_EVENT_NAME, {
      location: UserGuideStartLocation.MissionControl,
    });
    try {
      const result = await startUserGuide({ id: nextObjective.id });
      if (result?.data?.userGuideStart.status === UserGuideStatus.InProgress) {
        openLearnPanel(nextObjective.groupId);
      }
    } finally {
      setIsStarting(false);
    }
  }, [nextObjective, startUserGuide, openLearnPanel, trackSegmentEvent]);

  const handleContinue = useCallback(() => {
    if (!nextObjective) return;
    openLearnPanel(nextObjective.groupId);
  }, [nextObjective, openLearnPanel]);

  const getSectionLabel = () => {
    if (nextObjective?.isInProgress) return "Continue objective";
    if (completedCount > 0) return "Next objective";
    return "Start first objective";
  };

  const sectionLabel = getSectionLabel();

  const cardProps = {
    title: "Missions progress",
    headerPrefix: <Icon src={GraduationHat} />,
    headerSuffix,
  };

  if (loading && !totalCount) {
    return (
      <Card {...cardProps}>
        <Box direction="column" gap="large">
          <Skeleton height={20} width="100%" />
          <Skeleton height={60} width="100%" />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card {...cardProps}>
        <MissingDataBanner
          text="Couldn't load missions data. Please try to refresh or come back later."
          refreshHandler={refetch}
          fullWidth
        />
      </Card>
    );
  }

  return (
    <Card {...cardProps}>
      <Box direction="column" gap="xx-large">
        {!isAllCompleted && (
          <Box align="center" gap="x-large">
            <Box align="center" gap="small" shrink="0">
              <Icon src={Clock} color="secondary" />
              <Typography tag="span" variant="p-body3" color="secondary">
                {overallTimeToComplete} left
              </Typography>
            </Box>
            <ProgressBar
              current={completedCount}
              max={totalCount}
              isPercentage
              fullWidth
              ariaLabel="Missions progress"
            />
          </Box>
        )}

        {!isAllCompleted && nextObjective && (
          <Box direction="column" gap="medium">
            <Typography tag="p" variant="p-t7">
              {sectionLabel}
            </Typography>

            <TileWrapper>
              <TileContent direction="row" align="center" gap="medium">
                <Box direction="column" gap="small" grow="1" zeroMinWidth>
                  <TextEllipsis tooltip={nextObjective.title} tooltipWidthMode="maxWidthSm">
                    {(props) => (
                      <Typography {...props} tag="span" variant="p-t7">
                        {nextObjective.title}
                      </Typography>
                    )}
                  </TextEllipsis>
                  <Box align="center" gap="small">
                    <Icon src={Clock} color="secondary" />
                    <Tooltip
                      active={!!nextObjective.timeToComplete}
                      on={(props) => (
                        <Typography {...props} tag="span" variant="p-body3" color="secondary">
                          {nextObjective.timeToComplete}
                        </Typography>
                      )}
                    >
                      Estimated time to complete: {nextObjective.timeToComplete}
                    </Tooltip>
                  </Box>
                </Box>
                <Button
                  variant="primary"
                  size="small"
                  onPress={nextObjective.isInProgress ? handleContinue : handleStart}
                  loading={isStarting && !nextObjective.isInProgress}
                >
                  {nextObjective.isInProgress ? "Continue" : "Start"}
                </Button>
              </TileContent>
            </TileWrapper>
          </Box>
        )}

        {isAllCompleted && (
          <TileWrapper>
            <EmptyState icon={ConfettiColored} caption="All missions completed!" padding="large" />
          </TileWrapper>
        )}
      </Box>
    </Card>
  );
};

export default MissionControlMissionsProgress;
